import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchProperties as fetchPropertiesApi } from "../api/properties";
import { Property } from "../types/property";

type Status = "idle" | "loading" | "succeeded" | "failed";

interface PropertiesState {
  items: Property[];
  status: Status;
  error: string | null;
}

const initialState: PropertiesState = {
  items: [],
  status: "idle",
  error: null,
};

export const fetchProperties = createAsyncThunk<
  Property[],
  void,
  {
    state: { properties: PropertiesState };
    rejectValue: string;
  }
>(
  "properties/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const properties = await fetchPropertiesApi();
      return properties;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch properties");
    }
  },
  {
    // React Strict Mode replays effects in development. The pending reducer is
    // synchronous, so this condition prevents the replay from issuing a
    // second identical request.
    condition: (_, { getState }) =>
      getState().properties.status !== "loading",
  }
);

const propertiesSlice = createSlice({
  name: "properties",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProperties.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload;
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.status = state.items.length > 0 ? "succeeded" : "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch properties";
      });
  },
});

export default propertiesSlice.reducer;
